<script setup lang="ts">
import { CAD } from '@sharefe/share3d-engine'
import type {
  CameraRig,
  CadPlugin,
  DisplayUnit,
  DxfExporterOptions,
  EngineEventMap,
  FeetFormat,
  PotreeEngine,
} from '@sharefe/share3d-engine'
import type { ShallowRef } from 'vue'
import { computed, inject, reactive, ref, watch } from 'vue'

const { TYPES } = CAD

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
  }>(),
  {
    title: 'CAD 测绘',
    subtitle: 'CAD 平面图绘制与编辑',
  },
)

const engine = inject('engine') as ShallowRef<PotreeEngine | null>
const cadPlugin = computed(() => {
  return engine.value?.plugins.get('cad') as PanelCadPlugin | undefined
})

type DrawToolType = (typeof CAD.DrawToolType)[keyof typeof CAD.DrawToolType]
type EditToolType = (typeof CAD.EditToolType)[keyof typeof CAD.EditToolType]
type LineTypeValue = (typeof CAD.LineType)[keyof typeof CAD.LineType]
type PanelCadPlugin = Omit<CadPlugin, 'activateDrawTool'> & {
  activateDrawTool(params: { toolType: DrawToolType; entityData: {} }): void
}

const drawType = ref<DrawToolType>()
const lineType = ref<LineTypeValue>(CAD.LineType.Solid)
const lineTypeOptions: Array<{ label: string; value: LineTypeValue }> = [
  { label: '实线', value: CAD.LineType.Solid },
  { label: '虚线', value: CAD.LineType.Dash },
  { label: '点划线', value: CAD.LineType.DashDot },
  { label: '双划线', value: CAD.LineType.DoubleDash },
]
const drawOptions = [
  { label: '多段线', value: CAD.DrawToolType.Polyline, toolName: TYPES.DrawPolylineTool },
  { label: '圆弧', value: CAD.DrawToolType.Arc, toolName: TYPES.DrawArcTool },
  { label: '圆', value: CAD.DrawToolType.Circle, toolName: TYPES.DrawCircleTool },
  { label: '矩形', value: CAD.DrawToolType.Rect, toolName: TYPES.DrawRectTool },
  { label: '多边形', value: CAD.DrawToolType.Polygon, toolName: TYPES.DrawPolygonTool },
  { label: '椭圆', value: CAD.DrawToolType.Ellipse, toolName: TYPES.DrawEllipseTool },
  { label: '射线', value: CAD.DrawToolType.Ray, toolName: TYPES.DrawRayTool },
  { label: '样条曲线', value: CAD.DrawToolType.Spline, toolName: TYPES.DrawSplineTool },
  { label: '文字标注', value: CAD.DrawToolType.Text, toolName: TYPES.DrawTextTool },
  { label: '长度标注', value: CAD.DrawToolType.Dimension, toolName: TYPES.DrawDimensionTool },
  { label: '多重引线', value: CAD.DrawToolType.MLeader, toolName: TYPES.DrawMLeaderTool },
  { label: '角度标注', value: CAD.DrawToolType.AngularDimension, toolName: TYPES.DrawAngularDimensionTool },
]
const drawToolNameMap = new Map<symbol, DrawToolType>(
  drawOptions.map((item) => [item.toolName, item.value] as const),
)

const handleDrawTypeChange = (value: DrawToolType) => {
  console.log(`切换绘制工具: ${value}`)
  if (drawType.value === value) {
    cadPlugin.value?.exitDrawTool(drawType.value)
    drawType.value = undefined
    return
  }
  drawType.value = value
  cadPlugin.value?.activateDrawTool({
    toolType: value,
    entityData: createDrawEntityData(),
  })
}

const createDrawEntityData = () => ({
  textHeight: 0.5,
  lineType: lineType.value,
})

const handleLineTypeChange = (value: LineTypeValue) => {
  if (lineType.value === value) return
  lineType.value = value
  if (!drawType.value) return

  cadPlugin.value?.exitDrawTool(drawType.value)
  cadPlugin.value?.activateDrawTool({
    toolType: drawType.value,
    entityData: createDrawEntityData(),
  })
}

const editType = ref<EditToolType>()
const editOptions = [
  { label: '移动', value: CAD.EditToolType.Move, toolName: TYPES.MoveEntityTool },
  { label: '复制', value: CAD.EditToolType.Copy, toolName: TYPES.CopyEntityTool },
  { label: '旋转', value: CAD.EditToolType.Rotate, toolName: TYPES.RotateEntityTool },
  { label: '偏移', value: CAD.EditToolType.Offset, toolName: TYPES.OffsetEntityTool },
  { label: '闭合', value: CAD.EditToolType.Close, toolName: TYPES.CloseEntityTool },
  { label: '打断', value: CAD.EditToolType.Break, toolName: TYPES.BreakEntityTool },
  { label: '修剪', value: CAD.EditToolType.Trim, toolName: TYPES.TrimEntityTool },
  { label: '延伸', value: CAD.EditToolType.Extend, toolName: TYPES.ExtendEntityTool },
  { label: '合并', value: CAD.EditToolType.Join, toolName: TYPES.JoinEntityTool },
]
const editToolNameMap = new Map<symbol, EditToolType>(
  editOptions.map((item) => [item.toolName, item.value] as const),
)

const handleEditTypeChange = (value: EditToolType) => {
  if (editType.value === value) {
    cadPlugin.value?.exitEditTool(editType.value)
    editType.value = undefined
    return
  }
  editType.value = value
  cadPlugin.value?.activateEditTool({ toolType: value })
}

const isOrthogonalEnabled = ref(false)
const toggleOrthogonal = () => {
  isOrthogonalEnabled.value = !isOrthogonalEnabled.value
  cadPlugin.value?.setOrthogonalEnabled(isOrthogonalEnabled.value)
}

const isCaptureEnabled = ref(false)
const toggleCapture = () => {
  isCaptureEnabled.value = !isCaptureEnabled.value
  cadPlugin.value?.setCaptureEnabled(isCaptureEnabled.value)
}

// ── 导出设置对话框 ─────────────────────────────────────────────────────────────

const showExportDialog = ref(false)

const today = new Date()
const defaultDate =
  `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}` +
  `-${String(today.getDate()).padStart(2, '0')}`

const exportOptions = reactive({
  enableFrame: true,
  displayUnit: 'mm' as DisplayUnit,
  feetFormat: 'decimal' as FeetFormat,
  paperSize: 'A3' as 'A1' | 'A2' | 'A3' | 'A4',
  scale: 100,
  productName: '赛尔装修扫描仪',
  projectName: '',
  drawingUnit: '赛尔智控',
  drawingName: '原始测绘平面图',
  drawingNumber: 'YSPM-01',
  date: defaultDate,
  logoPath: './SHARE3DCAM_logo.png',
})

const displayUnitOptions: Array<{ label: string; value: DisplayUnit }> = [
  { label: '米 (m)', value: 'm' },
  { label: '厘米 (cm)', value: 'cm' },
  { label: '毫米 (mm)', value: 'mm' },
  { label: '英寸 (in)', value: 'in' },
  { label: '英尺 (ft)', value: 'ft' },
]

const feetFormatOptions: Array<{ label: string; value: FeetFormat }> = [
  { label: '十进制英尺', value: 'decimal' },
  { label: '英尺-英寸', value: 'feet-inches' },
]

const paperSizeOptions = [
  { label: 'A1 (841×594mm)', value: 'A1' },
  { label: 'A2 (594×420mm)', value: 'A2' },
  { label: 'A3 (420×297mm)', value: 'A3' },
  { label: 'A4 (297×210mm)', value: 'A4' },
]

const scaleOptions = [20, 50, 75, 100, 200, 400, 500, 1000].map((s) => ({
  label: `1:${s}`,
  value: s,
}))

const handleExportDxf = () => {
  showExportDialog.value = true
}

const sanitizeDxfFilename = (value: string) => {
  const filename = value.trim().replace(/[\\/:*?"<>|]+/g, '_')
  return filename || 'cad_project'
}

const getExportFilename = () => {
  const baseName = exportOptions.projectName || exportOptions.drawingName || 'cad_project'
  return `${sanitizeDxfFilename(baseName)}.dxf`
}

const doExport = () => {
  const handle = cadPlugin.value?.getActive()
  if (!handle) {
    console.error('DXF导出失败：未找到当前 CAD 资产')
    return
  }

  try {
    const serializedData = handle.dataManager.serialize()
    const data = {
      layers: handle.dataManager.layers,
      entities: handle.dataManager.entities,
      plane: serializedData.plane,
    } as CAD.ICADMetadata

    const filename = getExportFilename()
    const dxfOptions: DxfExporterOptions = {
      displayUnit: exportOptions.displayUnit,
      feetFormat: exportOptions.displayUnit === 'ft' ? exportOptions.feetFormat : undefined,
    }
    const dxfString = exportOptions.enableFrame
      ? CAD.DxfExporter.exportWithFrame(
          data,
          {
            paperSize: exportOptions.paperSize,
            scale: exportOptions.scale,
            productName: exportOptions.productName,
            projectName: exportOptions.projectName,
            drawingUnit: exportOptions.drawingUnit,
            drawingName: exportOptions.drawingName,
            drawingNumber: exportOptions.drawingNumber,
            date: exportOptions.date,
            logoPath: exportOptions.logoPath,
          },
          dxfOptions,
        )
      : CAD.DxfExporter.export(data, dxfOptions)

    CAD.DxfExporter.downloadDxf(filename, dxfString)
    showExportDialog.value = false
    console.log(`DXF导出完成，文件名: ${filename}`)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.error(`DXF导出失败：${message}`)
  }
}

const handleParseDxf = async (event: Event) => {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) {
    return
  }

  try {
    const handle = cadPlugin.value?.getActive()
    if (!handle) {
      throw new Error('未找到当前 CAD 资产')
    }

    const before = handle.dataManager.entities.length
    handle.importDxf(await file.text(), {
      recordHistory: true,
      mergeLayers: true,
    })
    const imported = handle.dataManager.entities.length - before
    if (imported <= 0) {
      throw new Error('DXF 未解析出可导入的图元')
    }

    if (handle.boundingBox) {
      engine.value?.views.getActive()?.getSlot<CameraRig>('cameraRig')?.fitToBox(handle.boundingBox)
    }
    console.log(`DXF导入完成，图元数: ${imported}`)
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error)
    console.error(`DXF导入失败：${message}`)
  } finally {
    input.value = ''
  }
}

const clearAll = () => {
  const handle = cadPlugin.value?.getActive()
  if (!handle) {
    return
  }
  const entityIds = handle.dataManager.entities.map((e: { id: string }) => e.id)
  if (entityIds.length > 0) {
    handle.deleteEntities(entityIds, { recordHistory: true })
    console.log(`已清除所有 CAD 图元，数量: ${entityIds.length}`)
  }
}

const clearToolSelection = () => {
  drawType.value = undefined
  editType.value = undefined
}

const handleToolChanged = ({ curToolName }: EngineEventMap['cad.toolChanged']) => {
  if (typeof curToolName !== 'symbol') {
    clearToolSelection()
    return
  }

  const nextDrawType = drawToolNameMap.get(curToolName)
  if (nextDrawType !== undefined) {
    drawType.value = nextDrawType
    editType.value = undefined
    return
  }

  const nextEditType = editToolNameMap.get(curToolName)
  if (nextEditType !== undefined) {
    editType.value = nextEditType
    drawType.value = undefined
    return
  }

  clearToolSelection()
}

watch(
  engine,
  (engineInstance, _, onCleanup) => {
    clearToolSelection()

    if (!engineInstance) {
      return
    }

    engineInstance.events.on('cad.toolChanged', handleToolChanged)
    onCleanup(() => {
      engineInstance.events.off('cad.toolChanged', handleToolChanged)
    })
  },
  { immediate: true },
)
</script>

<template>
  <details class="panel-section" open>
    <summary class="panel-section-summary">
      <span class="summary-main">
        <span class="panel-section-title">CAD 测绘</span>
        <span class="panel-section-subtitle">CAD 平面图绘制与编辑</span>
      </span>
      <span class="summary-side">
        <span class="summary-chevron" aria-hidden="true">›</span>
      </span>
    </summary>

    <div class="panel-section-body stacked-panel-body">
      <!-- 图元绘制 -->
      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">图元绘制</span>
            <span class="panel-section-subtitle">在切片平面上绘制 CAD 图元</span>
          </span>
          <span class="summary-chevron" aria-hidden="true">›</span>
        </summary>
        <div class="panel-subsection-body">
          <div class="button-grid button-grid-three">
            <button
              v-for="item in drawOptions"
              :key="item.value"
              class="panel-button"
              :class="{ active: drawType === item.value }"
              @click="handleDrawTypeChange(item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
            </button>
          </div>
        </div>
      </details>

      <!-- 线型 -->
      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">线型</span>
            <span class="panel-section-subtitle">设置后续绘制图元使用的线型</span>
          </span>
          <span class="summary-chevron" aria-hidden="true">›</span>
        </summary>
        <div class="panel-subsection-body">
          <div class="button-grid button-grid-four">
            <button
              v-for="item in lineTypeOptions"
              :key="item.value"
              class="panel-button"
              :class="{ active: lineType === item.value }"
              @click="handleLineTypeChange(item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
            </button>
          </div>
        </div>
      </details>

      <!-- 图元编辑 -->
      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">图元处理</span>
            <span class="panel-section-subtitle">移动、复制、旋转、偏移等图元处理</span>
          </span>
          <span class="summary-chevron" aria-hidden="true">›</span>
        </summary>
        <div class="panel-subsection-body">
          <div class="button-grid button-grid-three">
            <button
              v-for="item in editOptions"
              :key="item.value"
              class="panel-button"
              :class="{ active: editType === item.value }"
              @click="handleEditTypeChange(item.value)"
            >
              <span class="panel-button-label">{{ item.label }}</span>
            </button>
          </div>
        </div>
      </details>

      <!-- 辅助工具 -->
      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">辅助工具</span>
            <span class="panel-section-subtitle">正交、捕捉、撤销重做</span>
          </span>
          <span class="summary-chevron" aria-hidden="true">›</span>
        </summary>
        <div class="panel-subsection-body">
          <div class="button-grid">
            <button
              class="panel-button"
              :class="{ active: isOrthogonalEnabled }"
              @click="toggleOrthogonal"
            >
              <span class="panel-button-label">正交</span>
              <span class="panel-button-meta">{{ isOrthogonalEnabled ? '已开启' : '已关闭' }}</span>
            </button>
            <button
              class="panel-button"
              :class="{ active: isCaptureEnabled }"
              @click="toggleCapture"
            >
              <span class="panel-button-label">捕捉</span>
              <span class="panel-button-meta">{{ isCaptureEnabled ? '已开启' : '已关闭' }}</span>
            </button>
          </div>
        </div>
      </details>

      <!-- DXF 导入导出 -->
      <details class="panel-subsection" open>
        <summary class="panel-subsection-summary">
          <span class="summary-main">
            <span class="panel-subsection-title">DXF 交换</span>
            <span class="panel-section-subtitle">导入导出 AutoCAD DXF 文件</span>
          </span>
          <span class="summary-chevron" aria-hidden="true">›</span>
        </summary>
        <div class="panel-subsection-body">
          <div class="button-grid">
            <button
              class="panel-button panel-button-subtle"
              @click="handleExportDxf"
            >
              <span class="panel-button-label">导出 DXF</span>
            </button>
            <label class="panel-button panel-button-subtle" style="cursor: pointer;">
              <input
                type="file"
                accept=".dxf"
                @change="handleParseDxf"
                style="display: none;"
              />
              <span class="panel-button-label">导入 DXF</span>
            </label>
          </div>
          <button
            class="panel-button panel-button-wide panel-button-subtle"
            style="margin-top: 10px; color: #ff9595;"
            @click="clearAll"
          >
            <span class="panel-button-label">清空所有</span>
          </button>
        </div>
      </details>
    </div>
  </details>

  <!-- 导出设置对话框 -->
  <n-modal v-model:show="showExportDialog" preset="dialog" title="导出 DXF 设置" style="width: 480px">
    <n-flex vertical style="gap: 12px; padding: 4px 0;">
      <n-flex align="center" justify="space-between">
        <span style="min-width: 72px;">长度单位</span>
        <n-select
          v-model:value="exportOptions.displayUnit"
          :options="displayUnitOptions"
          style="flex: 1;"
        />
      </n-flex>

      <n-flex
        v-if="exportOptions.displayUnit === 'ft'"
        align="center"
        justify="space-between"
      >
        <span style="min-width: 72px;">英尺格式</span>
        <n-select
          v-model:value="exportOptions.feetFormat"
          :options="feetFormatOptions"
          style="flex: 1;"
        />
      </n-flex>

      <n-flex align="center" justify="space-between">
        <span>添加图框</span>
        <n-switch v-model:value="exportOptions.enableFrame" />
      </n-flex>

      <template v-if="exportOptions.enableFrame">
        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">纸张大小</span>
          <n-select
            v-model:value="exportOptions.paperSize"
            :options="paperSizeOptions"
            style="flex: 1;"
          />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">出图比例</span>
          <n-select
            v-model:value="exportOptions.scale"
            :options="scaleOptions"
            style="flex: 1;"
          />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">项目名称</span>
          <n-input v-model:value="exportOptions.projectName" placeholder="请输入项目名称" style="flex: 1;" />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">制图单位</span>
          <n-input v-model:value="exportOptions.drawingUnit" style="flex: 1;" />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">图名</span>
          <n-input v-model:value="exportOptions.drawingName" style="flex: 1;" />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">图号</span>
          <n-input v-model:value="exportOptions.drawingNumber" style="flex: 1;" />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">日期</span>
          <n-input :value="exportOptions.date" readonly style="flex: 1;" />
        </n-flex>

        <n-flex align="center" justify="space-between">
          <span style="min-width: 72px;">Logo 路径</span>
          <n-input v-model:value="exportOptions.logoPath" placeholder="./share_logo.png" style="flex: 1;" />
        </n-flex>
      </template>

      <n-flex justify="end" style="margin-top: 8px; gap: 8px;">
        <n-button @click="showExportDialog = false">取消</n-button>
        <n-button type="primary" @click="doExport">导出</n-button>
      </n-flex>
    </n-flex>
  </n-modal>
</template>

<style scoped>
.button-grid-three {
  grid-template-columns: repeat(3, minmax(0, 1fr));
}

.button-grid-four {
  grid-template-columns: repeat(4, minmax(0, 1fr));
}
</style>
